// Cache des couches lourdes (plan 12/09 D5 — « le premier chargement plante parfois, le
// second doit être instantané ») : DVF et SITADEL d'une commune sont retenus côté client,
// en mémoire (la session) ET dans la Cache API (survit au rechargement de page), sous une clé
// commune + version. La Cache API est absente en navigation privée ou sur un vieux navigateur :
// tout y est fail-soft, la mémoire suffit alors. Une réponse « gated » n'est jamais retenue
// (prédicat `cachable`) — le droit d'accès se résout en base à chaque requête, pas ici.
//
// Revue codex P2 sur #262 (12/09) : une couche RÉSERVÉE (option `jeton`) est bornée par le
// jeton qui l'a obtenue — la clé porte une empreinte de l'identité ET de l'échéance du JWT (un
// autre compte, ou le même compte avec un jeton renouvelé, ne relit jamais l'entrée), et
// l'entrée expire AU PLUS TARD à l'échéance du jeton (jamais servie après ; un jeton déjà échu
// ne retient rien). La déconnexion et le changement de compte vident tout (AuthContext →
// `viderCacheCartes`). Les couches publiques (sans option `jeton`) gardent la clé simple.
// Le JWT n'est jamais VÉRIFIÉ ici (le serveur le fait) : on ne fait qu'en LIRE deux champs
// pour partitionner et borner le cache — et sans le stocker.
// 09/10 : les permis SITADEL portent désormais adresse, parcelle(s) et précision ('parcelle' |
// 'ban' | 'section') — lots D de la nuit (#636, #640) ; sans ce cran, un navigateur qui avait vu la
// page la veille aurait servi 24 h les permis d'avant le géocodage (constaté à 2 h 15 en recette).
export const CACHE_VERSION = '2026-10-09'          // à incrémenter quand la forme d'une charge utile change
const NOM_CACHE = 'terobs-cartes'
const ORIGINE_CLE = 'https://cartes.terobs.local/'
const TTL_DEFAUT_MS = 24 * 3600 * 1000              // les ingestions sont quotidiennes au plus

export type OrigineCache = 'memoire' | 'cache' | 'reseau'
export interface OptionsCache<T> {
  ttlMs?: number
  version?: string
  /** false → la valeur est servie mais pas retenue (ex. réponse gated) */
  cachable?: (v: T) => boolean
  /** couche RÉSERVÉE : le jeton de session (null = anonyme) borne et partitionne le cache */
  jeton?: string | null
}
export interface SujetJeton { cle: string; expMs: number | null }

const memoire = new Map<string, { expire: number; valeur: unknown }>()
const enCours = new Map<string, Promise<{ valeur: unknown; origine: OrigineCache }>>()   // déduplique deux appels simultanés

function cleComplete(cle: string, version?: string) {
  return `${ORIGINE_CLE}${encodeURIComponent(version ?? CACHE_VERSION)}/${encodeURIComponent(cle)}`
}

/** Empreinte courte et stable (FNV-1a) — jamais réversible en pratique, jamais le jeton lui-même. */
function empreinte(s: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h.toString(16).padStart(8, '0')
}

function decoderCharge(token: string): Record<string, unknown> | null {
  const parts = token.split('.')
  if (parts.length !== 3) return null
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4))
    const obj = JSON.parse(new TextDecoder().decode(Uint8Array.from(bin, c => c.charCodeAt(0))))
    return obj && typeof obj === 'object' ? obj as Record<string, unknown> : null
  } catch { return null }
}

/** Identité (sub ou email) et échéance d'un JWT, LUES sans vérification ; null si absent ou illisible. */
export function sujetDuJeton(token: string | null | undefined): SujetJeton | null {
  if (!token) return null
  const p = decoderCharge(token)
  if (!p) return null
  const identite = String(p.sub ?? p.email ?? '')
  const expMs = typeof p.exp === 'number' ? p.exp * 1000 : null
  return { cle: empreinte(`${identite}|${expMs ?? ''}`), expMs }
}

function cacheApi(): CacheStorage | null {
  try {
    const c = (globalThis as { caches?: CacheStorage }).caches
    return c && typeof c.open === 'function' ? c : null
  } catch { return null }
}

async function lireCacheApi<T>(url: string): Promise<{ valeur: T; expire: number } | null> {
  const c = cacheApi()
  if (!c) return null
  try {
    const store = await c.open(NOM_CACHE)
    const res = await store.match(url)
    if (!res) return null
    const expire = Number(res.headers.get('x-terobs-expire') || 0)
    if (!expire || expire <= Date.now()) { await store.delete(url); return null }
    return { valeur: await res.json() as T, expire }
  } catch { return null }
}

async function ecrireCacheApi(url: string, valeur: unknown, expire: number) {
  const c = cacheApi()
  if (!c) return
  try {
    const store = await c.open(NOM_CACHE)
    await store.put(url, new Response(JSON.stringify(valeur), {
      headers: { 'content-type': 'application/json', 'x-terobs-expire': String(expire) },
    }))
  } catch { /* quota, navigation privée : la mémoire suffit */ }
}

/** Charge `cle` depuis la mémoire, sinon la Cache API, sinon `charger()` (puis retient le résultat). */
export async function chargerAvecCache<T>(
  cle: string, charger: () => Promise<T>, options: OptionsCache<T> = {},
): Promise<{ valeur: T; origine: OrigineCache }> {
  const ttl = options.ttlMs ?? TTL_DEFAUT_MS
  const now = Date.now()
  // Couche réservée : partition par sujet du jeton, borne à son échéance.
  let suffixe = ''
  let borne: number | null = null
  if ('jeton' in options) {
    if (options.jeton) {
      const s = sujetDuJeton(options.jeton)
      // jeton illisible ou déjà échu : on ne sait pas borner → rien n'est lu ni retenu
      if (!s || (s.expMs != null && s.expMs <= now)) return { valeur: await charger(), origine: 'reseau' }
      suffixe = `|${s.cle}`
      borne = s.expMs
    } else {
      suffixe = '|anon'
    }
  }
  const url = cleComplete(cle + suffixe, options.version)
  const m = memoire.get(url)
  if (m && m.expire > now) return { valeur: m.valeur as T, origine: 'memoire' }
  if (m) memoire.delete(url)

  // Deux appels simultanés (StrictMode, remontage) partagent LA MÊME promesse : pas de
  // promesse dérivée, qui rejetterait dans le vide (rejet non géré) quand le réseau échoue.
  const deja = enCours.get(url) as Promise<{ valeur: T; origine: OrigineCache }> | undefined
  if (deja) return { valeur: (await deja).valeur, origine: 'memoire' }

  const travail = (async () => {
    const c = await lireCacheApi<T>(url)
    if (c) {
      memoire.set(url, { expire: c.expire, valeur: c.valeur })
      return { valeur: c.valeur, origine: 'cache' as OrigineCache }
    }
    const valeur = await charger()
    if (!options.cachable || options.cachable(valeur)) {
      // jamais au-delà de l'échéance du jeton qui a obtenu la réponse
      const expire = Math.min(Date.now() + ttl, borne ?? Number.POSITIVE_INFINITY)
      memoire.set(url, { expire, valeur })
      void ecrireCacheApi(url, valeur, expire)
    }
    return { valeur, origine: 'reseau' as OrigineCache }
  })()
  enCours.set(url, travail)
  try {
    return await travail
  } finally {
    enCours.delete(url)
  }
}

/** Efface tout (mémoire + Cache API) — déconnexion, changement de compte. */
export async function viderCacheCartes() {
  window.dispatchEvent(new Event('terobs:purge-session'))
  memoire.clear()
  enCours.clear()
  const c = cacheApi()
  if (!c) return
  try { await c.delete(NOM_CACHE) } catch { /* rien à effacer */ }
}

/** Tests : simule une nouvelle page (mémoire vide, Cache API intacte). */
export function _reinitialiserPourTests() {
  memoire.clear()
  enCours.clear()
}
